// =============================================================================
// services/apple-auth.ts — bare-RN "Sign in with Apple" shim.
//
// MIGRATION (blueprint §1, Apple-auth swap + Risk note): the Expo app used
// `expo-apple-authentication` + `expo-crypto` to (a) check availability,
// (b) generate a random raw nonce, SHA-256 hash it, hand the HASH to Apple, and
// (c) forward the RAW nonce to the backend, which re-hashes and verifies it
// (strict-when-present anti-replay). Bare RN uses
// `@invertase/react-native-apple-authentication`.
//
// CRITICAL CONTRACT PRESERVATION — the nonce:
//   The invertase lib ALSO does nonce-by-default: when you pass NO `nonce`, it
//   generates a random raw nonce, SHA-256 hashes it before sending to Apple, and
//   returns the RAW (unhashed) value back as `response.nonce`
//   (see AppleRequestResponse.nonce: "This value will be SHA256 hashed before
//   sending to Apple"). So the exact same scheme the Expo flow built by hand is
//   built into the lib — we just READ `response.nonce` and forward it to the
//   backend as `rawNonce`. No expo-crypto needed; the byte-level contract
//   (SHA256(rawNonce) === token.nonce claim) is identical.
//
// The native "Sign in with Apple" CAPABILITY must be added in Xcode by a human
// (Signing & Capabilities → + Capability → Sign in with Apple) and the entitlement
// must ship in the signed binary — this file is the JS half only.
//
// Lazy-require + graceful: the screens stay tsc-clean and crash-free before the
// pod is installed (P4 `pod install`). When the native module is absent,
// isAppleAuthAvailable() reads false and the Apple button never renders.
// =============================================================================
import { Platform } from 'react-native';

// The invertase module type surface we use (kept local so this file needs no
// type import from the package — it stays tsc-clean even uninstalled).
interface AppleRequestResponseLike {
  nonce: string;
  identityToken: string | null;
  authorizationCode: string | null;
  email: string | null;
  fullName: null | {
    givenName: string | null;
    familyName: string | null;
    middleName: string | null;
    [k: string]: string | null;
  };
}

type AppleAuthModule = {
  isSupported: boolean;
  performRequest: (options: any) => Promise<AppleRequestResponseLike>;
  Operation: { IMPLICIT: number; LOGIN: number; REFRESH: number; LOGOUT: number };
  Scope: { EMAIL: number; FULL_NAME: number };
};

// Lazy-require so an uninstalled pod can't crash the bundle at import time.
let _appleAuth: AppleAuthModule | null = null;
let _resolved = false;
function getModule(): AppleAuthModule | null {
  if (_resolved) return _appleAuth;
  _resolved = true;
  try {
    const mod = require('@invertase/react-native-apple-authentication');
    _appleAuth = (mod.appleAuth ?? mod.default ?? mod) as AppleAuthModule;
  } catch {
    _appleAuth = null;
  }
  return _appleAuth;
}

/** The native AppleButton component (or null when the module isn't present). */
export function getAppleButton(): any | null {
  try {
    const mod = require('@invertase/react-native-apple-authentication');
    return mod.AppleButton ?? null;
  } catch {
    return null;
  }
}

/**
 * SYNC iOS-only availability — `isSupported` is a plain SYNC boolean on the
 * invertase module (false on Android / sim < iOS 13 / when the pod is missing).
 * Screens use this to initialize render state so the Apple button is present on
 * the FIRST paint (no async probe → no pop-in after mount/interaction).
 */
export function isAppleAuthAvailableSync(): boolean {
  if (Platform.OS !== 'ios') return false;
  return !!getModule()?.isSupported;
}

/**
 * Async availability — kept for tap-time re-verification (call sites that
 * `await`ed the old Expo `isAvailableAsync()` probe stay unchanged). Same
 * answer as the sync check above.
 */
export async function isAppleAuthAvailable(): Promise<boolean> {
  return isAppleAuthAvailableSync();
}

export interface AppleCredential {
  identityToken: string;
  /** RAW nonce (unhashed) — forward to the backend as `rawNonce`. */
  rawNonce: string;
  fullName: AppleRequestResponseLike['fullName'];
  email: string | undefined;
}

/** Thrown/returned shape so callers can detect a user cancel (Apple code 1001). */
export const APPLE_CANCELED = '1001';

/**
 * Run the Apple LOGIN request. Returns the credential, or null if the user
 * canceled. Throws on any other failure (so the screen can surface the native
 * reason — entitlement/provisioning errors show here).
 *
 * We pass NO `nonce` so the lib generates+hashes one for us and hands back the
 * raw value in `response.nonce` (see file header) — byte-identical to the old
 * expo-crypto flow.
 */
export async function performAppleLogin(): Promise<AppleCredential | null> {
  const mod = getModule();
  if (!mod) {
    throw new Error('Apple authentication is not available on this device.');
  }
  try {
    const res = await mod.performRequest({
      requestedOperation: mod.Operation.LOGIN,
      requestedScopes: [mod.Scope.FULL_NAME, mod.Scope.EMAIL],
    });

    if (!res.identityToken) {
      // Surface to the caller as a distinct (non-cancel) failure.
      throw new Error('Apple did not return an identity token. Please try again.');
    }

    return {
      identityToken: res.identityToken,
      // res.nonce is the RAW nonce (the lib SHA-256s a copy for Apple). This is
      // exactly what the backend re-hashes + verifies — preserve it verbatim.
      rawNonce: res.nonce,
      fullName: res.fullName,
      email: res.email ?? undefined,
    };
  } catch (e: any) {
    // User canceled the system sheet — not an error (matches the Expo flow's
    // swallowed ERR_REQUEST_CANCELED). invertase uses Apple code '1001'.
    if (e?.code === APPLE_CANCELED) return null;
    throw e;
  }
}
